import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, setBaseCurrency } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnvelopeSimple, PaperPlaneTilt, CircleNotch, Warning, CheckCircle, Eye } from "@phosphor-icons/react";

export default function Notificaciones() {
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState("");
  const [purchasingEmail, setPurchasingEmail] = useState("");
  const [weekly, setWeekly] = useState(false);
  const [baseCurrency, setBaseCurrencyState] = useState("CLP");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setEmail(r.data.recipient_email || "");
      setPurchasingEmail(r.data.purchasing_email || "");
      setWeekly(r.data.weekly_enabled || false);
      setBaseCurrencyState(r.data.base_currency || "CLP");
    });
  };

  useEffect(() => {
    load();
    api.get("/notificaciones/preview").then((r) => setPreview(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        recipient_email: email,
        weekly_enabled: weekly,
        purchasing_email: purchasingEmail,
        base_currency: baseCurrency,
      });
      setSettings(data);
      setBaseCurrency(data.base_currency);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notificaciones/enviar");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="notificaciones-page">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-md bg-[#EFF6FF] flex items-center justify-center">
          <EnvelopeSimple size={22} weight="bold" className="text-[#1E40AF]" />
        </span>
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Notificaciones</h1>
          <p className="text-[#475569] mt-1">Resumen semanal por email de lotes por vencer y liquidación.</p>
        </div>
      </div>

      {!settings.resend_configured && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-4 flex items-start gap-3" data-testid="resend-warning">
          <Warning size={22} weight="bold" className="text-[#92400E] shrink-0 mt-0.5" />
          <div className="text-sm text-[#92400E]">
            <span className="font-semibold">Resend no está configurado.</span> Para enviar emails, agrega tu{" "}
            <code className="bg-[#FEF3C7] px-1 rounded">RESEND_API_KEY</code> en el archivo{" "}
            <code className="bg-[#FEF3C7] px-1 rounded">backend/.env</code> y reinicia el servidor. Puedes obtenerla en resend.com.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-md p-6 space-y-5 h-fit">
          <h2 className="font-heading text-lg font-bold text-[#0F172A]">Configuración</h2>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Moneda base de la app</Label>
            <Select value={baseCurrency} onValueChange={setBaseCurrencyState}>
              <SelectTrigger data-testid="base-currency-select" className="bg-white border-[#E2E8F0]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(settings.supported_currencies || ["CLP", "USD", "EUR"]).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#94A3B8]">
              Moneda en la que se muestran todos los montos. Las compras en otra moneda se convierten con su tipo de cambio.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Email destinatario</Label>
            <Input
              type="email"
              data-testid="recipient-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tucorreo@empresa.com"
              className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-[#E2E8F0] p-4">
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Envío semanal automático</p>
              <p className="text-xs text-[#64748B]">Cada lunes a las 08:00.</p>
            </div>
            <Switch checked={weekly} onCheckedChange={setWeekly} data-testid="weekly-switch" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Email de compras (avisos de reposición)</Label>
            <Input
              type="email"
              data-testid="purchasing-email-input"
              value={purchasingEmail}
              onChange={(e) => setPurchasingEmail(e.target.value)}
              placeholder="compras@empresa.com"
              className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
            />
            <p className="text-xs text-[#94A3B8]">
              Cuando un producto cruce su punto de reorden por una salida, se enviará un aviso automático a este correo.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={save} disabled={saving} data-testid="save-settings" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {saving ? <CircleNotch size={18} className="animate-spin" /> : "Guardar"}
            </Button>
            <Button
              onClick={sendNow}
              disabled={sending}
              variant="outline"
              data-testid="send-now"
              className="border-[#E2E8F0]"
            >
              {sending ? <CircleNotch size={18} className="animate-spin mr-1.5" /> : <PaperPlaneTilt size={18} className="mr-1.5" />}
              Enviar ahora
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
            <Eye size={18} className="text-[#475569]" />
            <h2 className="font-heading text-lg font-bold text-[#0F172A]">Vista previa del email</h2>
          </div>
          <div className="p-4">
            {preview ? (
              <>
                <div className="flex gap-4 mb-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-[#9A3412] font-semibold">
                    <CheckCircle size={16} weight="bold" /> {preview.alertas} alertas
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#991B1B] font-semibold">
                    {preview.sugerencias} sugerencias de liquidación
                  </span>
                </div>
                <iframe
                  title="preview"
                  data-testid="email-preview"
                  srcDoc={preview.html}
                  className="w-full h-[420px] border border-[#E2E8F0] rounded-md bg-white"
                />
              </>
            ) : (
              <div className="flex items-center justify-center py-20">
                <CircleNotch size={24} className="animate-spin text-[#0F172A]" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
